import { Injectable } from "@angular/core";
import { ProvaDatasource, ItemProvaDatasource } from "../../models/UserData";
import { SignalrService } from "../signalr.service";
import { OperacaoSignalR } from "../../models/OperacaoSignalR";
import { EnumTipoObjeto } from "src/app/enums/EnumTipoObjeto";
import { ErrorHandlerService } from "../errorHandler.service";
import { StorageService } from "../storage.service";

@Injectable()
export class UsuarioProvasService {
  readonly databaseName = "userdata";
  readonly instituicoesCollectionName = "instituicoes";
  readonly bancasCollectionName = "bancas";
  readonly anosCollectionName = "anos";
  readonly tiposCollectionName = "tipos";
  readonly cargosCollectionName = "cargos";

  constructor(
    private signalrService: SignalrService,
    private errorHandlerService: ErrorHandlerService,
    private storageService: StorageService
  ) {
    signalrService
      .getMensagem()
      .subscribe((msg) => this.onMessageReceived(msg));
  }

  public async fromNuvem(provaDatasource: ProvaDatasource): Promise<void> {
    await this.storageService
      .upsertMany(
        provaDatasource.instituicoes,
        this.databaseName,
        this.instituicoesCollectionName,
        false
      )
      .then(() => {
        this.storageService
          .upsertMany(
            provaDatasource.bancas,
            this.databaseName,
            this.bancasCollectionName,
            false
          )
          .then(() => {
            this.storageService
              .upsertMany(
                provaDatasource.anos,
                this.databaseName,
                this.anosCollectionName,
                false
              )
              .then(() => {
                this.storageService
                  .upsertMany(
                    provaDatasource.tipos,
                    this.databaseName,
                    this.tiposCollectionName,
                    false
                  )
                  .then(() => {
                    this.storageService
                      .upsertMany(
                        provaDatasource.cargos,
                        this.databaseName,
                        this.cargosCollectionName,
                        false
                      )
                      .then(() => {})
                      .catch((error) => {
                        this.errorHandlerService.handleError(error);
                      });
                  })
                  .catch((error) => {
                    this.errorHandlerService.handleError(error);
                  });
              })
              .catch((error) => {
                this.errorHandlerService.handleError(error);
              });
          })
          .catch((error) => {
            this.errorHandlerService.handleError(error);
          });
      })
      .catch((error) => {
        this.errorHandlerService.handleError(error);
      });
  }

  public async incluirInstituicao(
    instituicao: ItemProvaDatasource,
    audit = true,
    sync = true
  ): Promise<void> {
    await this.storageService
      .upsert(
        instituicao,
        this.databaseName,
        this.instituicoesCollectionName,
        audit
      )
      .then((retorno) => {
        if (sync) {
          const mensagem = new OperacaoSignalR();
          mensagem.dados = instituicao;
          this.signalrService.enviarMensagem(
            mensagem,
            EnumTipoObjeto.Instituicao
          );
        }
      })
      .catch((error) => {
        this.errorHandlerService.handleError(error);
      });
  }

  public async incluirBanca(
    banca: ItemProvaDatasource,
    audit = true,
    sync = true
  ): Promise<void> {
    await this.storageService
      .upsert(banca, this.databaseName, this.bancasCollectionName, audit)
      .then((retorno) => {
        if (sync) {
          const mensagem = new OperacaoSignalR();
          mensagem.dados = banca;
          this.signalrService.enviarMensagem(mensagem, EnumTipoObjeto.Banca);
        }
      })
      .catch((error) => {
        this.errorHandlerService.handleError(error);
      });
  }

  public async incluirAno(
    ano: ItemProvaDatasource,
    audit = true,
    sync = true
  ): Promise<void> {
    await this.storageService
      .upsert(ano, this.databaseName, this.anosCollectionName, audit)
      .then((retorno) => {
        if (sync) {
          const mensagem = new OperacaoSignalR();
          mensagem.dados = ano;
          this.signalrService.enviarMensagem(mensagem, EnumTipoObjeto.Ano);
        }
      })
      .catch((error) => {
        this.errorHandlerService.handleError(error);
      });
  }

  public async incluirTipo(
    tipo: ItemProvaDatasource,
    audit = true,
    sync = true
  ): Promise<void> {
    await this.storageService
      .upsert(tipo, this.databaseName, this.tiposCollectionName, audit)
      .then((retorno) => {
        if (sync) {
          const mensagem = new OperacaoSignalR();
          mensagem.dados = tipo;
          this.signalrService.enviarMensagem(mensagem, EnumTipoObjeto.Tipo);
        }
      })
      .catch((error) => {
        this.errorHandlerService.handleError(error);
      });
  }

  public async incluirCargo(
    cargo: ItemProvaDatasource,
    audit = true,
    sync = true
  ): Promise<void> {
    await this.storageService
      .upsert(cargo, this.databaseName, this.cargosCollectionName, audit)
      .then((retorno) => {
        if (sync) {
          const mensagem = new OperacaoSignalR();
          mensagem.dados = cargo;
          this.signalrService.enviarMensagem(mensagem, EnumTipoObjeto.Cargo);
        }
      })
      .catch((error) => {
        this.errorHandlerService.handleError(error);
      });
  }

  public async carregarDatasource(): Promise<ProvaDatasource> {
    const consultas = new Array<Promise<any>>();
    const provaDatasource = new ProvaDatasource();

    consultas.push(
      this.storageService
        .list<ItemProvaDatasource>(
          this.databaseName,
          this.instituicoesCollectionName
        )
        .then((lista) => (provaDatasource.instituicoes = lista))
    );

    consultas.push(
      this.storageService
        .list<ItemProvaDatasource>(this.databaseName, this.bancasCollectionName)
        .then((lista) => (provaDatasource.bancas = lista))
    );

    consultas.push(
      this.storageService
        .list<ItemProvaDatasource>(this.databaseName, this.anosCollectionName)
        .then((lista) => (provaDatasource.anos = lista))
    );

    consultas.push(
      this.storageService
        .list<ItemProvaDatasource>(this.databaseName, this.tiposCollectionName)
        .then((lista) => (provaDatasource.tipos = lista))
    );

    consultas.push(
      this.storageService
        .list<ItemProvaDatasource>(this.databaseName, this.cargosCollectionName)
        .then((lista) => (provaDatasource.cargos = lista))
    );

    return await Promise.all(consultas)
      .then(() => provaDatasource)
      .catch((error) => {
        this.errorHandlerService.handleError(error);
        return provaDatasource;
      });
  }
  private onMessageReceived(msg: OperacaoSignalR): void {
    if (!msg) {
      return;
    }

    if (msg.tipoObjeto === EnumTipoObjeto.Instituicao) {
      this.incluirInstituicao(msg.dados, false);
    } else if (msg.tipoObjeto === EnumTipoObjeto.Banca) {
      this.incluirBanca(msg.dados, false);
    } else if (msg.tipoObjeto === EnumTipoObjeto.Ano) {
      this.incluirAno(msg.dados, false);
    } else if (msg.tipoObjeto === EnumTipoObjeto.Tipo) {
      this.incluirTipo(msg.dados, false);
    } else if (msg.tipoObjeto === EnumTipoObjeto.Cargo) {
      this.incluirCargo(msg.dados, false);
    }
  }
}
